/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { PlannedScheduledLocation } from './PlannedScheduledLocation';
import {
    PlannedScheduledLocationFromJSON,
    PlannedScheduledLocationFromJSONTyped,
    PlannedScheduledLocationToJSON,
    PlannedScheduledLocationToJSONTyped,
} from './PlannedScheduledLocation';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Operation } from './Operation';
import {
    OperationFromJSON,
    OperationFromJSONTyped,
    OperationToJSON,
    OperationToJSONTyped,
} from './Operation';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * A service offer is a commercial product proposed to a requestor, in response to a quotation request.
 * @export
 * @interface QuotationRequestServiceOffer
 */
export interface QuotationRequestServiceOffer {
    /**
     * Given name of the commercial product of the service offer made by the provider.
     * @type {string}
     * @memberof QuotationRequestServiceOffer
     */
    commercialProductName?: string;
    /**
     * Commercial product reference code of the service offer.
     * @type {string}
     * @memberof QuotationRequestServiceOffer
     */
    commercialProductCode?: string;
    /**
     * Start date and time of the validity period of the service offer.
     * @type {Date}
     * @memberof QuotationRequestServiceOffer
     */
    validityStartDateTime?: Date;
    /**
     * End date and time of the validity period of the service offer.
     * @type {Date}
     * @memberof QuotationRequestServiceOffer
     */
    validityEndDateTime?: Date;
    /**
     * Remarks related to the service offer, the provider wants to highlight.
     * @type {Array<string>}
     * @memberof QuotationRequestServiceOffer
     */
    remarks?: Array<string>;
    /**
     * The start location where the goods are shipped from.
     * @type {PlannedScheduledLocation}
     * @memberof QuotationRequestServiceOffer
     */
    startLocation?: PlannedScheduledLocation;
    /**
     * The end location where the goods are delivered.
     * @type {PlannedScheduledLocation}
     * @memberof QuotationRequestServiceOffer
     */
    endLocation?: PlannedScheduledLocation;
    /**
     * Incoterm requested by the customer. The Intercom (International Commercial Terms) is a 3 characters code defining the contractual terms between the seller and the buyer regarding the delivery of the goods and the payment of taxes. The International Chamber of Commerce (ICC) provides the list of these codes. This incoterm is the one given buy the seller to the service provider to apply the requested transport rule.
     * @type {string}
     * @memberof QuotationRequestServiceOffer
     */
    incoterm?: string;
    /**
     * Sale Operations proposed in the service offer. Some of them are mandatory. Others are optional.
     * @type {Array<Operation>}
     * @memberof QuotationRequestServiceOffer
     */
    saleOperations?: Array<Operation>;
    /**
     * The provider is the party proposing the service.
     * @type {Party}
     * @memberof QuotationRequestServiceOffer
     */
    provider?: Party;
    /**
     * 
     * @type {Link}
     * @memberof QuotationRequestServiceOffer
     */
    quotationRequest?: Link;
    /**
     * Calculated carbon footprint.
     * @type {Value}
     * @memberof QuotationRequestServiceOffer
     */
    carbonFootprint?: Value;
    /**
     * Special instructions applied to the service taken into account in the service offer.
     * @type {Array<SpecialInstruction>}
     * @memberof QuotationRequestServiceOffer
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Special characteristics describing the goods or the service taken into account in the service offer. For example, if the requested service has to be considered as dangerous, it should appear as a special characteristics.
     * @type {Array<SpecialCharacteristic>}
     * @memberof QuotationRequestServiceOffer
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * External references of the Service Offer can be used when they are defined in a legacy system that registers the service under its own reference identifier. This can be useful for searching the Service using the ID from the legacy app. Frequent ExternalReference Types: PROVIDER_ID.
     * @type {Array<ExternalReference>}
     * @memberof QuotationRequestServiceOffer
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * List of documents used for the service offer, such as sales instructions.
     * @type {Array<Document>}
     * @memberof QuotationRequestServiceOffer
     */
    documents?: Array<Document>;
    /**
     * List of contacts related to the service offer. This can include persons or teams to be contacted about the service offer.
     * @type {Array<Contact>}
     * @memberof QuotationRequestServiceOffer
     */
    contacts?: Array<Contact>;
    /**
     * UUIDv7 identifier of the object.
     * @type {string}
     * @memberof QuotationRequestServiceOffer
     */
    id?: string;
}

/**
 * Check if a given object implements the QuotationRequestServiceOffer interface.
 */
export function instanceOfQuotationRequestServiceOffer(value: object): value is QuotationRequestServiceOffer {
    return true;
}

export function QuotationRequestServiceOfferFromJSON(json: any): QuotationRequestServiceOffer {
    return QuotationRequestServiceOfferFromJSONTyped(json, false);
}

export function QuotationRequestServiceOfferFromJSONTyped(json: any, ignoreDiscriminator: boolean): QuotationRequestServiceOffer {
    if (json == null) {
        return json;
    }
    return {
        
        'commercialProductName': json['commercialProductName'] == null ? undefined : json['commercialProductName'],
        'commercialProductCode': json['commercialProductCode'] == null ? undefined : json['commercialProductCode'],
        'validityStartDateTime': json['validityStartDateTime'] == null ? undefined : (new Date(json['validityStartDateTime'])),
        'validityEndDateTime': json['validityEndDateTime'] == null ? undefined : (new Date(json['validityEndDateTime'])),
        'remarks': json['remarks'] == null ? undefined : json['remarks'],
        'startLocation': json['startLocation'] == null ? undefined : PlannedScheduledLocationFromJSON(json['startLocation']),
        'endLocation': json['endLocation'] == null ? undefined : PlannedScheduledLocationFromJSON(json['endLocation']),
        'incoterm': json['incoterm'] == null ? undefined : json['incoterm'],
        'saleOperations': json['saleOperations'] == null ? undefined : ((json['saleOperations'] as Array<any>).map(OperationFromJSON)),
        'provider': json['provider'] == null ? undefined : PartyFromJSON(json['provider']),
        'quotationRequest': json['quotationRequest'] == null ? undefined : LinkFromJSON(json['quotationRequest']),
        'carbonFootprint': json['carbonFootprint'] == null ? undefined : ValueFromJSON(json['carbonFootprint']),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'id': json['id'] == null ? undefined : json['id'],
    };
}

export function QuotationRequestServiceOfferToJSON(json: any): QuotationRequestServiceOffer {
    return QuotationRequestServiceOfferToJSONTyped(json, false);
}

export function QuotationRequestServiceOfferToJSONTyped(value?: QuotationRequestServiceOffer | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'commercialProductName': value['commercialProductName'],
        'commercialProductCode': value['commercialProductCode'],
        'validityStartDateTime': value['validityStartDateTime'] == null ? value['validityStartDateTime'] : value['validityStartDateTime'].toISOString(),
        'validityEndDateTime': value['validityEndDateTime'] == null ? value['validityEndDateTime'] : value['validityEndDateTime'].toISOString(),
        'remarks': value['remarks'],
        'startLocation': PlannedScheduledLocationToJSON(value['startLocation']),
        'endLocation': PlannedScheduledLocationToJSON(value['endLocation']),
        'incoterm': value['incoterm'],
        'saleOperations': value['saleOperations'] == null ? undefined : ((value['saleOperations'] as Array<any>).map(OperationToJSON)),
        'provider': PartyToJSON(value['provider']),
        'quotationRequest': LinkToJSON(value['quotationRequest']),
        'carbonFootprint': ValueToJSON(value['carbonFootprint']),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'id': value['id'],
    };
}

